/**
 * confluenceSignal.ts -- MER Algo Footprints
 *
 * Phase 2 v1: confluenceEngine schema.
 *
 * The confluence layer scores deterministic agreement across the existing
 * directional engines. It does NOT create new setups, replace setupEngine,
 * alter liquidityEngine behavior, or override deterministic setup logic.
 *
 * v1 contributors (5 engines, each weighted in computeConfluence):
 *   - regime              25%
 *   - smc                 25%
 *   - gamma               20%
 *   - institutionalFlow   20%
 *   - traps               10%
 *
 * Liquidity is NOT a directional voter in v1. Liquidity zones are
 * anchoring/contextual information; forcing them into bullish/bearish
 * buckets risks double-counting logic already partially captured by SMC.
 * Liquidity continues to reach the AI thesis via the existing LIQUIDITY
 * ZONES prompt section.
 */

export type ConfluenceBias = 'bullish' | 'bearish' | 'neutral' | 'no_signal';

export type ConfluenceEngineName =
  | 'regime'
  | 'smc'
  | 'gamma'
  | 'institutionalFlow'
  | 'traps';

/**
 * A single engine's directional vote in the confluence score.
 *
 * - `weight` is the engine's static weight from the configured weighting
 *   scheme (regime 0.25, smc 0.25, gamma 0.20, flow 0.20, traps 0.10).
 *   It is independent of whether the engine produced a directional read
 *   this cycle. An engine that returns 'no_signal' or 'neutral' still
 *   carries its weight, but contributes 0 to the directional score.
 *
 * - `confidence` is the engine's own self-reported confidence in its
 *   bias, in [0, 1]. We use it to scale the directional contribution
 *   (a low-confidence bullish vote moves the score less than a
 *   high-confidence one).
 *
 * - `reason` is a one-line attribution for the prompt and audit log.
 */
export interface ConfluenceContribution {
  engine:      ConfluenceEngineName;
  bias:        ConfluenceBias;
  weight:      number;       // 0..1, static per-engine weight
  confidence:  number;       // 0..1, engine's self-reported confidence
  reason:      string;       // one-line attribution
}

/**
 * Aggregated confluence read.
 *
 * - `score` is the signed weighted sum of directional contributions, in
 *   [-1, +1]. Positive means net bullish, negative bearish. Magnitude is
 *   how strongly the contributing engines agreed.
 *
 * - `confidence` is the aggregated confidence in the bias call, in
 *   [0, 0.90]. Capped at 0.90 because engines can always disagree --
 *   confluence is never deterministic certainty.
 *
 * - `contributions` is every voting engine's individual read, in the
 *   order the contributors are configured. Useful for prompt evidence
 *   and future attribution analytics.
 *
 * - `dissenting` is the list of engines whose bias contradicts the
 *   majority direction. Empty when the majority is 'neutral' or
 *   'no_signal'. Surfaces the "minority report" so the AI can weigh
 *   uncertainty.
 *
 * - `evidence` is a human-readable summary list for the prompt and UI.
 */
export interface ConfluenceSignal {
  symbol:        string;
  bias:          ConfluenceBias;
  score:         number;       // -1..+1, signed weighted sum
  confidence:    number;       // 0..0.90, capped
  contributions: ConfluenceContribution[];
  dissenting:    ConfluenceEngineName[];
  evidence:      string[];
  timestamp:     number;       // Unix ms when computed
}

// ── Configured weights ──────────────────────────────────────────────────────
// These are the v1 weights agreed in the design discussion. They sum to 1.0.
// Re-evaluation will be driven by production attribution data in v2, not by
// preference.
export const CONFLUENCE_WEIGHTS: Record<ConfluenceEngineName, number> = {
  regime:            0.25,
  smc:               0.25,
  gamma:             0.20,
  institutionalFlow: 0.20,
  traps:             0.10,
};

// Sanity check (no runtime cost in production; tree-shaken if unused).
// Kept inline so a future weight change can't silently break the invariant.
if (
  Math.abs(
    Object.values(CONFLUENCE_WEIGHTS).reduce((a, b) => a + b, 0) - 1.0,
  ) > 1e-9
) {
  // eslint-disable-next-line no-console
  console.error('[confluence] CONFLUENCE_WEIGHTS do not sum to 1.0');
}

// Confluence confidence cap. Engines can disagree -- never claim certainty.
export const CONFLUENCE_CONFIDENCE_CAP = 0.90;
